import Immutable from 'immutable';
import {Component} from '@packages/systems/wfdl';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {
  getElementWithAncestors,
  getPageComponentFromComponentMap,
} from '@packages/utilities/platform';
import {
  getBindingContextBoundary,
  getAdjacentBindingContextConnectionOptionsForElement,
  type BindingContextBoundaryType,
  type BindingContextConnectionOption,
} from './boundary-creators';
import {memoize, once} from '@packages/utilities/memo';

import type {Gateway, GatewayRuntimeEnvironment} from '../types';
import {isPageRootTypeReference, type BindingContext} from '../primitives';
import {dataSources} from '@packages/systems/dynamo/sources';
import {buildGateways} from '../buildGateways';

import {type CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import findPageBindingContext from './findPageBindingContext';
import consolidateBindingContext, {
  type ConsolidatedBindingContext,
} from './consolidateBindingContext';
import {
  PageQuerySpecification,
  ElementQuerySpecification,
  RuntimeInfo,
  QueryPath,
  QueryPaths,
  ExpressionEvaluationInterface,
} from '@packages/systems/dynamo/source-types';

import {BindingSourceIndex} from './binding-sources';

type DeferredBindingContextBoundaries = Immutable.Map<
  string,
  BindingContextBoundaryType
>;
type ActiveDeferredBoundary = BindingContextBoundaryType | null;

type GraphBoundaryAction = Readonly<{
  action: 'deferred' | 'traversed';
  boundary: Readonly<BindingContextBoundaryType>;
}>;

const noDeferredBoundaries: DeferredBindingContextBoundaries = Immutable.Map();

/**
 * A QuerySpecification for when a binding context boundary has been traversed.
 *
 * Traversal is the moment when we encounter the boundary element of a
 * previously created and deferred binding context boundary and will update
 * the active binding context accordingly. We want to update `queryPathPrefix`
 * in the query builder and apply any query paths defined for the boundary
 * element.
 */
type TraversalQuerySpecification = {
  action: 'traversed';
  queryPathPrefix: QueryPath;
  queryPaths?: QueryPaths;
  relative: boolean;
  hideQueryPathPrefix: boolean;
};

/**
 * A QuerySpecification for when a binding context boundary has been deferred.
 *
 * Deferral is the moment when we encounter an element that creates a binding
 * context boundary that we defer to be traversed later. At this point we want
 * to apply any query paths defined for the boundary creator element.
 */
type DeferralQuerySpecification = {
  action: 'deferred';
  queryPaths: QueryPaths;
};

type QuerySpecification =
  | Readonly<TraversalQuerySpecification>
  | Readonly<DeferralQuerySpecification>;

// public interface of BindingContextGraphNode
export interface BindingContextGraphNode {
  getQuerySpecifications(
    runtimeInfo: RuntimeInfo,
    exprEvaluationContext: ExpressionEvaluationInterface
  ): QuerySpecification[];
  getBindingSourceIndex(): BindingSourceIndex | null;
  getNextNode(element: ExpressionElement): BindingContextGraphNode | null;
}

export class InternalBindingContextGraphNode implements BindingContextGraphNode {
  #bindingContext: BindingContext | '<ROOT_CONTEXT>';
  #activeDeferredBoundary: ActiveDeferredBoundary;
  #deferredBoundaries: DeferredBindingContextBoundaries;
  #parentNode: InternalBindingContextGraphNode | null;
  #gateways: Gateway[];
  traversedAndDeferredBoundaries: ReadonlyArray<GraphBoundaryAction>;

  constructor({
    bindingContext,
    activeDeferredBoundary,
    deferredBoundaries,
    parentNode,
    gateways,
    traversedAndDeferredBoundaries,
  }: {
    bindingContext: BindingContext | '<ROOT_CONTEXT>';
    activeDeferredBoundary?: ActiveDeferredBoundary;
    deferredBoundaries?: DeferredBindingContextBoundaries;
    parentNode?: InternalBindingContextGraphNode | null;
    gateways: Gateway[];
    traversedAndDeferredBoundaries?: GraphBoundaryAction[];
  }) {
    this.#bindingContext = bindingContext;
    this.#activeDeferredBoundary = activeDeferredBoundary || null;
    this.#deferredBoundaries = deferredBoundaries || noDeferredBoundaries;
    this.#parentNode = parentNode || null;
    this.#gateways = gateways;
    this.traversedAndDeferredBoundaries = traversedAndDeferredBoundaries || [];
  }

  /**
   * Normalizes representations of root binding contexts in the #bindingContext
   * property to null (i.e. the '<ROOT_CONTEXT>' value and binding contexts with
   * page root type references).
   *
   * This allows downstream code to handle graph nodes with no meaningful
   * effective binding contexts with a simple null check.
   *
   * @private
   */
  get #effectiveBindingContext(): BindingContext | null {
    const bindingContext = this.#bindingContext;
    if (
      bindingContext === '<ROOT_CONTEXT>' ||
      isPageRootTypeReference(bindingContext.type)
    ) {
      return null;
    }
    return bindingContext;
  }

  getBindingContext = once((): ConsolidatedBindingContext | null => {
    if (!this.#effectiveBindingContext) {
      return null;
    }
    const parentEffectiveBindingContext = this.#parentNode
      ? this.#parentNode.#effectiveBindingContext
      : null;
    return consolidateBindingContext(
      this.#effectiveBindingContext,
      parentEffectiveBindingContext,
      this.#gateways
    );
  });

  getParentBindingContext = once((): ConsolidatedBindingContext | null => {
    return this.#parentNode ? this.#parentNode.getBindingContext() : null;
  });

  getBindingSourceIndex = once((): BindingSourceIndex | null => {
    if (!this.#effectiveBindingContext) {
      return null;
    }
    return new BindingSourceIndex(
      this.#effectiveBindingContext,
      this.#gateways
    );
  });

  getQuerySpecifications(
    runtimeInfo: RuntimeInfo,
    exprEvaluationContext: ExpressionEvaluationInterface
  ): QuerySpecification[] {
    if (this.traversedAndDeferredBoundaries.length > 0) {
      const outputQuerySpecifications: QuerySpecification[] = [];

      for (const {action, boundary} of this.traversedAndDeferredBoundaries) {
        const {nextBindingContext, boundaryType, boundaryCreatorElement} =
          boundary;

        if (
          nextBindingContext &&
          (nextBindingContext.variant === 'Entity' ||
            nextBindingContext.variant === 'List') &&
          nextBindingContext.query
        ) {
          const {
            queryPathPrefix,
            boundaryCreatorQueryPaths,
            boundaryElementQueryPaths,
            hideQueryPathPrefix = false,
            relative = false,
          } = nextBindingContext.query as ElementQuerySpecification;

          if (action === 'deferred' && boundaryCreatorQueryPaths) {
            const queryPaths =
              typeof boundaryCreatorQueryPaths === 'function'
                ? boundaryCreatorQueryPaths(
                    boundaryCreatorElement,
                    runtimeInfo,
                    this.getBindingSourceIndex(),
                    exprEvaluationContext
                  )
                : boundaryCreatorQueryPaths;

            outputQuerySpecifications.push({action: 'deferred', queryPaths});
          }

          if (
            (action === 'traversed' && boundaryType === 'startAt') ||
            /*
              Handles the special case commerce order wrapper binding contexts
              are startAfter boundaries where the boundary creator is also the
              boundary element, while being treated like startAt boundaries by
              the query builder. This is done by treating the moment of deferral of such
              boundaries also as the traversal.

              Note that these commerce order wrappers are the only startAfter
              boundaries in our product at the time of writing this comment
              2023-08-04.
            */
            (action === 'deferred' && boundary === this.#activeDeferredBoundary)
          ) {
            const traversalQuerySpecification: TraversalQuerySpecification = {
              action: 'traversed',
              queryPathPrefix:
                typeof queryPathPrefix === 'function'
                  ? queryPathPrefix(
                      boundaryCreatorElement,
                      runtimeInfo,
                      this.#parentNode?.getBindingSourceIndex() || null,
                      exprEvaluationContext
                    )
                  : queryPathPrefix,
              hideQueryPathPrefix,
              relative,
            };

            if (boundaryElementQueryPaths) {
              traversalQuerySpecification.queryPaths =
                typeof boundaryElementQueryPaths === 'function'
                  ? boundaryElementQueryPaths(
                      boundaryCreatorElement,
                      runtimeInfo,
                      this.#parentNode?.getBindingSourceIndex() || null,
                      exprEvaluationContext
                    )
                  : boundaryElementQueryPaths;
            }

            outputQuerySpecifications.push(traversalQuerySpecification);
          }
        }
      }

      return outputQuerySpecifications;
    }

    if (
      this.#bindingContext !== '<ROOT_CONTEXT>' &&
      this.#bindingContext.variant === 'Page' &&
      this.#bindingContext.query
    ) {
      const querySpec: PageQuerySpecification = this.#bindingContext.query;
      const queryPathPrefix: QueryPath =
        typeof querySpec.queryPathPrefix === 'function'
          ? querySpec.queryPathPrefix(runtimeInfo)
          : querySpec.queryPathPrefix;
      return [
        {
          action: 'traversed',
          queryPathPrefix,
          hideQueryPathPrefix: false,
          relative: false,
        },
      ];
    }

    return [];
  }

  getElementConnectionOptions(
    element: ExpressionElement
  ): BindingContextConnectionOption[] {
    return getAdjacentBindingContextConnectionOptionsForElement(
      element,
      this.#bindingContext,
      this.#gateways
    );
  }

  getBindingContextBoundary(
    element: ExpressionElement
  ): BindingContextBoundaryType | null {
    return getBindingContextBoundary(
      element,
      this.#gateways,
      this.#bindingContext
    );
  }

  traverseBoundary(
    boundary: BindingContextBoundaryType,
    traversedAndDeferredBoundaries: GraphBoundaryAction[]
  ): InternalBindingContextGraphNode | null {
    const {nextBindingContext} = boundary;

    if (nextBindingContext === null) {
      // When the binding context boundary has a null binding context,
      // there can be no further traversal of the binding context graph.
      return null;
    }

    traversedAndDeferredBoundaries.push({action: 'traversed', boundary});

    return new InternalBindingContextGraphNode({
      bindingContext: nextBindingContext,
      // current node is the parent of the destination node
      parentNode: this,
      gateways: this.#gateways,
      traversedAndDeferredBoundaries,
    });
  }

  getImmediateDescendantNode = once(
    (): InternalBindingContextGraphNode | null => {
      if (this.#activeDeferredBoundary) {
        return this.traverseBoundary(this.#activeDeferredBoundary, []);
      }
      return this;
    }
  );

  getNextNode(
    element: ExpressionElement
  ): InternalBindingContextGraphNode | null {
    const descendantNode = this.getImmediateDescendantNode();

    if (descendantNode === null) {
      return null;
    }

    // ensure that any boundary actions recorded in getImmediateDescendantNode
    // are carried over to the node that is eventually returned
    const traversedAndDeferredBoundaries: GraphBoundaryAction[] =
      descendantNode !== this
        ? [...descendantNode.traversedAndDeferredBoundaries]
        : [];

    const newBoundary = descendantNode.getBindingContextBoundary(element);

    let updatedDeferredBoundaries = this.#deferredBoundaries;

    if (newBoundary) {
      updatedDeferredBoundaries = this.#deferredBoundaries.set(
        newBoundary.boundaryElementId,
        newBoundary
      );

      traversedAndDeferredBoundaries.push({
        action: 'deferred',
        boundary: newBoundary,
      });
    }

    const deferredBoundary = updatedDeferredBoundaries.get(element.id);

    if (!deferredBoundary) {
      return descendantNode.setDeferredBoundaries(
        updatedDeferredBoundaries,
        traversedAndDeferredBoundaries
      );
    }

    return deferredBoundary.boundaryType === 'startAt'
      ? descendantNode.traverseBoundary(
          deferredBoundary,
          traversedAndDeferredBoundaries
        )
      : descendantNode.setActiveDeferredBoundary(
          deferredBoundary,
          traversedAndDeferredBoundaries
        );
  }

  setActiveDeferredBoundary(
    nextActiveDeferredBoundary: BindingContextBoundaryType,
    traversedAndDeferredBoundaries: GraphBoundaryAction[]
  ): InternalBindingContextGraphNode {
    return new InternalBindingContextGraphNode({
      bindingContext: this.#bindingContext,
      activeDeferredBoundary: nextActiveDeferredBoundary,
      parentNode: this.#parentNode,
      gateways: this.#gateways,
      traversedAndDeferredBoundaries,
    });
  }

  setDeferredBoundaries(
    nextDeferredBoundaries: DeferredBindingContextBoundaries,
    traversedAndDeferredBoundaries: GraphBoundaryAction[]
  ): InternalBindingContextGraphNode {
    if (
      this.#activeDeferredBoundary === null &&
      this.#deferredBoundaries === nextDeferredBoundaries
    ) {
      // prevent unnecessary creation of identical instances
      return this;
    }

    return new InternalBindingContextGraphNode({
      bindingContext: this.#bindingContext,
      deferredBoundaries: nextDeferredBoundaries,
      parentNode: this.#parentNode,
      gateways: this.#gateways,
      traversedAndDeferredBoundaries,
    });
  }

  isMarkedNotImplemented(): boolean {
    return (
      this.#bindingContext !== '<ROOT_CONTEXT>' &&
      isMarkedNotImplemented(this.#bindingContext, this.#gateways)
    );
  }
}

export const getElementBindingContextNode = memoize(
  _getElementBindingContextNode
);

function _getElementBindingContextNode(
  elementId: string,
  currentComponent: Component.ComponentType,
  componentMap: Component.ComponentMap,
  page: PageRecordType | null,
  collections: CollectionsType,
  runtimeEnvironment: GatewayRuntimeEnvironment
): InternalBindingContextGraphNode | null {
  const rootElement = Component.getRender(currentComponent);
  const elementPath = getElementWithAncestors(rootElement)(elementId);

  if (elementPath.length === 0) {
    // tree did not contain an element that matched elementId
    return null;
  }

  const pageComponent = getPageComponentFromComponentMap(componentMap);

  const initialBindingContextNode =
    page && currentComponent === pageComponent
      ? getPageRootBindingContextNode(page, collections, runtimeEnvironment)
      : getComponentRootBindingContextNode(collections, runtimeEnvironment);

  return deriveBindingContextFromElementPath(
    elementPath,
    initialBindingContextNode
  );
}

function deriveBindingContextFromElementPath(
  elementPath: ExpressionElement[],
  initialBindingContextNode: InternalBindingContextGraphNode
): InternalBindingContextGraphNode | null {
  let bindingContextNode = initialBindingContextNode;

  for (let pathIndex = 0; pathIndex < elementPath.length; pathIndex++) {
    const element = elementPath[pathIndex];
    // @ts-expect-error - TS2345 - Argument of type 'ExpressionElement | undefined' is not assignable to parameter of type 'ExpressionElement'.
    const nextBindingContextNode = bindingContextNode.getNextNode(element);

    if (nextBindingContextNode === null) {
      // When the next node in binding context graph is null,
      // we can return early as there cannot be relative contexts of null.
      return null;
    }

    bindingContextNode = nextBindingContextNode;
  }

  if (bindingContextNode.isMarkedNotImplemented()) {
    return null;
  }

  return bindingContextNode;
}

function isMarkedNotImplemented(
  bindingContext: BindingContext,
  gateways: Gateway[]
): boolean {
  const typeReference = bindingContext.type;
  return gateways.some((gateway) => {
    const bindingContextType = gateway.getBindingContextType(typeReference);
    return bindingContextType?.notImplemented;
  });
}

export const getPageBindingContextNode = memoize(getPageRootBindingContextNode);

function getPageRootBindingContextNode(
  page: PageRecordType,
  collections: CollectionsType,
  runtimeEnvironment: GatewayRuntimeEnvironment
): InternalBindingContextGraphNode {
  const gateways = buildGateways(dataSources, collections, runtimeEnvironment);
  const bindingContext =
    findPageBindingContext(gateways, page) || '<ROOT_CONTEXT>';
  return new InternalBindingContextGraphNode({
    bindingContext,
    gateways,
  });
}

function getComponentRootBindingContextNode(
  collections: CollectionsType,
  runtimeEnvironment: GatewayRuntimeEnvironment
): InternalBindingContextGraphNode {
  const gateways = buildGateways(dataSources, collections, runtimeEnvironment);
  return new InternalBindingContextGraphNode({
    bindingContext: '<ROOT_CONTEXT>',
    gateways,
  });
}
